import type { ApiErrorBody, ErrorCode } from "@dentos/contracts";

/** Error from the API, with the stable code and any field-level details. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | "network",
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
  }

  /** Field errors as { "profile.phone": "Invalid phone" } for forms. */
  get fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    return Object.fromEntries(
      (this.details as { path?: string; message?: string }[]).filter((d) => d.path).map((d) => [d.path!, d.message ?? "Invalid"]),
    );
  }
}

let onUnauthenticated: (() => void) | null = null;
/** The session provider registers this so an expired session sends the user to the login page. */
export const setUnauthenticatedHandler = (fn: () => void) => {
  onUnauthenticated = fn;
};

async function request<T>(method: string, url: string, body?: unknown): Promise<{ data: T; page?: { limit: number; nextCursor: string | null } }> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? {} : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network", "Cannot reach the server. Check your internet connection.");
  }
  if (res.status === 204) return { data: undefined as T };
  const json = (await res.json().catch(() => null)) as (ApiErrorBody & { data: T; page?: never }) | null;
  if (!res.ok) {
    const e = json?.error;
    // A proxy/load balancer answering for a down API (502/503/504 without our error envelope).
    if (!e && res.status >= 502 && res.status <= 504) {
      throw new ApiError(0, "network", "The server is not responding. Please try again in a moment.");
    }
    // Public pages (booking, patient portal) handle their own sign-in; only staff screens go to the staff login.
    if (res.status === 401 && e?.code === "unauthenticated" && !url.startsWith("/api/auth/login") && !url.startsWith("/api/auth/mfa/verify") && !url.startsWith("/api/public/")) onUnauthenticated?.();
    throw new ApiError(res.status, e?.code ?? "internal", e?.message ?? `Request failed (${res.status})`, e?.details, e?.requestId);
  }
  return json as { data: T; page?: { limit: number; nextCursor: string | null } };
}

export const api = {
  get: async <T>(url: string) => (await request<T>("GET", url)).data,
  page: <T>(url: string) => request<T[]>("GET", url),
  post: async <T>(url: string, body: unknown = {}) => (await request<T>("POST", url, body)).data,
  put: async <T>(url: string, body: unknown = {}) => (await request<T>("PUT", url, body)).data,
  patch: async <T>(url: string, body: unknown = {}) => (await request<T>("PATCH", url, body)).data,
  del: async (url: string) => {
    await request("DELETE", url);
  },
};

export const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : "";
};
